import { EventType } from "@/analytics/analytics-events";
import {
  CancelFloorPlanGenerationFlowProperties,
  GenerateFloorPlanEventProperties,
} from "@/analytics/floor-plan-generation-events-properties";
import { getFullDeepLinkURL } from "@/components/common/deep-link/deep-link-encoding";
import { addFeatureFlagToUrl } from "@/components/common/deep-link/deep-link-utils";
import { FeedbackKey } from "@/components/common/feedback/feedback-context";
import { isMetric } from "@/components/common/settings-context/distance-unit-utils";
import { BetaBadge } from "@/components/ui/beta-badge";
import { ElementIconType } from "@/components/ui/icons";
import { Features } from "@/store/features/features";
import { selectGetFeatureValueIfEnabled } from "@/store/features/features-slice";
import { OnlyIElementsStore } from "@/store/ielements-slice-selector";
import { selectIsAllowedToBuyTokens } from "@/store/user-selectors";
import { createDashboardSubscriptionUrl } from "@/utils/redirects";
import { Analytics } from "@faro-lotv/foreign-observers";
import { GUID, assert, generateGUID } from "@faro-lotv/foundation";
import {
  IElement,
  IElementGenericPointCloud,
  isIElementGenericPointCloud,
} from "@faro-lotv/ielement-types";
import {
  PointCloudType,
  selectChildDepthFirst,
  selectCompanyId,
  selectDashboardUrl,
  selectIElement,
  selectPointCloudType,
  selectProjectAccessLevel,
  selectRootId,
} from "@faro-lotv/project-source";
import {
  FloorplanGenerationMeasurementUnit,
  FloorplanServiceVendor,
  MutationGenerateFloorplansForPointCloud,
  ProjectAccessLevel,
  ProjectApi,
  createMutationAddEmptyLayerSection,
  createMutationGenerateFloorplansForPointCloud,
  isElementAllowedForFloorplanGeneration,
} from "@faro-lotv/service-wires";
import { ContextMenuAction, ContextMenuActionType } from "../../action-types";
import {
  FloorPlanGenerationBetaMarketingFeedback,
  FloorPlanGenerationStartedDialog,
  FloorPlanOptionsDialog,
  FloorPlanOrderSummaryDialog,
  RequestMoreTokensDialog,
} from "./generate-floor-plan-dialogs";

/**
 * Trigger the generation of floorplan(s) for the selected model
 */
export const GENERATE_FLOORPLAN: ContextMenuAction = {
  type: ContextMenuActionType.generateFloorplan,
  label: "Generate floor plan",
  labelRight: <BetaBadge />,
  icon: ElementIconType.SheetNewIcon,
  disabledMessageForNode: (element, state) => {
    const isPointCloudSupported = !!selectChildDepthFirst(
      element,
      (el) =>
        isElementAllowedForFloorplanGeneration(el) &&
        !!selectPointCloudForFloorplan(el)(state),
    )(state);

    if (!isPointCloudSupported) {
      return "Floor plan generation not available for this point cloud format. Currently supports FARO scan data only.";
    }
  },
  handler: async ({
    elementID,
    state,
    apiClients: { projectApiClient, coreApiClient },
    distanceUnit,
    errorHandlers: { handleErrorWithToast },
    createDialog,
    requestFeedback,
  }) => {
    Analytics.track(EventType.startFloorPlanGenerationFlow);

    try {
      let measurementUnit = isMetric(distanceUnit)
        ? FloorplanGenerationMeasurementUnit.metric
        : FloorplanGenerationMeasurementUnit.imperial;

      const [availableTokens, requiredTokens] = await Promise.all([
        coreApiClient
          .getTokenBalance(projectApiClient.projectId)
          .then((response) => response.data.balance),
        projectApiClient.calculateTokensForFloorPlanGeneration({
          datasetId: elementID,
        }),
      ]);
      const remainingTokens = availableTokens - requiredTokens;

      const vendor = parseVendorFromFeatureValue(
        selectGetFeatureValueIfEnabled(Features.FloorPlanVendor)(state),
      );

      let industryName: string | undefined;

      const wantsToTry = await createDialog({
        // Not having a title breaks the layout of the "X" button
        title: " ",
        size: "l",
        content: (
          <FloorPlanGenerationBetaMarketingFeedback
            onIndustryChanged={(industry) => (industryName = industry)}
          />
        ),
        confirmText: "Try Now",
        showXButton: true,
        hideCancelButton: true,
        sx: {
          ".MuiDialog-paper": {
            // We need more space than the largest dialog currently provides
            width: "100%",
            maxWidth: "1000px",
          },
        },
      });
      if (!wantsToTry) {
        Analytics.track<CancelFloorPlanGenerationFlowProperties>(
          EventType.cancelFloorPlanGenerationFlow,
          {
            step: "BETA industry feedback",
          },
        );
        return;
      }

      if (availableTokens < requiredTokens) {
        const dashboardUrl = selectDashboardUrl(state);
        const companyId = selectCompanyId(state);
        assert(dashboardUrl && companyId, "Expected user to be in a company");

        await createDialog({
          title: "Floor plan generation",
          content: (
            <RequestMoreTokensDialog
              availableTokens={availableTokens}
              requiredTokens={requiredTokens}
              isAllowedToBuyTokens={selectIsAllowedToBuyTokens(state)}
              subscriptionsUrl={createDashboardSubscriptionUrl(
                dashboardUrl,
                companyId,
              )}
            />
          ),
          showXButton: true,
          hideCancelButton: true,
          hideConfirmButton: true,
        });

        Analytics.track<CancelFloorPlanGenerationFlowProperties>(
          EventType.cancelFloorPlanGenerationFlow,
          {
            step: "Insufficient tokens",
          },
        );
        return;
      }

      const hasSelectedOptions = await createDialog({
        title: "Floor plan generation",
        content: (
          <FloorPlanOptionsDialog
            availableTokens={availableTokens}
            requiredTokens={requiredTokens}
            initialMeasurementUnit={measurementUnit}
            onMeasurementUnitChanged={(newUnit) => {
              measurementUnit = newUnit;
            }}
          />
        ),
        confirmText: "Continue",
        showXButton: true,
        allowToCloseWithoutCancelling: true,
      });

      if (!hasSelectedOptions) {
        Analytics.track<CancelFloorPlanGenerationFlowProperties>(
          EventType.cancelFloorPlanGenerationFlow,
          {
            step: "Floor plan generation options",
          },
        );
        return;
      }

      const hasConfirmedOrder = await createDialog({
        title: "Order summary",
        content: (
          <FloorPlanOrderSummaryDialog
            availableTokens={availableTokens}
            requiredTokens={requiredTokens}
            remainingTokens={remainingTokens}
          />
        ),
        confirmText: "Submit Order",
        showXButton: true,
        allowToCloseWithoutCancelling: true,
        async onConfirm() {
          Analytics.track<GenerateFloorPlanEventProperties>(
            EventType.generateFloorPlan,
            {
              industryName,
              units: measurementUnit,
            },
          );

          try {
            const rootId = selectRootId(state);
            assert(rootId, "Can only create empty layer with root ID.");

            const element = selectIElement(elementID)(state);

            const datasetSection = selectChildDepthFirst(
              element,
              isElementAllowedForFloorplanGeneration,
            )(state);
            assert(datasetSection, "Can't find suitable dataset section.");

            /** Pointcloud to use to generate the floor plan */
            const pcElement = selectPointCloudForFloorplan(element)(state);
            assert(pcElement, "Unable to find suitable point cloud.");

            const projectAccessLevel = selectProjectAccessLevel(state);

            const layerId = generateGUID();

            // Generating a link that will open the Viewer looking at the point cloud the floor plan is generated for
            const projectShareLink = addFeatureFlagToUrl(
              getFullDeepLinkURL({
                id: datasetSection.id,
                // We want to open the export mode directly, to make sure that floor plan designers of the third-party provider
                // do not miss the ortho photo export functionality. It is important that they use it, so the scale of the
                // manually created floor plan is correct.
                mode: "export",
              }),
              // Allow the third party supplier to access volume exports, to help improve the accuracy of the scale
              Features.AllowViewerExport,
            ).toString();

            await startFloorplanGeneration({
              projectApiClient,
              layerId,
              rootId,
              sectionId: datasetSection.id,
              pointcloudElement: pcElement,
              isPrivate: projectAccessLevel === ProjectAccessLevel.private,
              projectShareLink,
              measurementUnit,
              vendor,
            });
            return true;
          } catch (error) {
            handleErrorWithToast({
              title: "Unable to generate floor plan",
              error,
            });
            return false;
          }
        },
      });

      if (!hasConfirmedOrder) {
        Analytics.track<CancelFloorPlanGenerationFlowProperties>(
          EventType.cancelFloorPlanGenerationFlow,
          {
            step: "Order summary",
          },
        );
        return;
      }

      await createDialog({
        title: null,
        content: (
          <FloorPlanGenerationStartedDialog remainingTokens={remainingTokens} />
        ),
        confirmText: "Ok",
        hideCancelButton: true,
      });

      requestFeedback({
        key: FeedbackKey.floorPlanGeneration,
        data: {
          question: "How would you rate the order experience?",
          numberedLabels: [
            "Not satisfied",
            undefined,
            undefined,
            undefined,
            "Very satisfied",
          ],
        },
      });
    } catch (error) {
      handleErrorWithToast({ title: "Unable to generate floor plan", error });
    }
  },
};

type StartFloorplanGenerationArgs = Pick<
  MutationGenerateFloorplansForPointCloud,
  "isPrivate" | "projectShareLink" | "measurementUnit" | "vendor"
> & {
  /** Client to use */
  projectApiClient: ProjectApi;

  /** ID of the layer section to create the point cloud in */
  layerId: GUID;

  /** ID of the root element */
  rootId: GUID;

  /** ID of the section to append the empty layer to */
  sectionId: GUID;

  /** Point cloud to generate the floor plan(s) */
  pointcloudElement: IElementGenericPointCloud;
};

async function startFloorplanGeneration({
  projectApiClient,
  layerId,
  rootId,
  sectionId,
  pointcloudElement,
  isPrivate,
  projectShareLink,
  measurementUnit,
  vendor,
}: StartFloorplanGenerationArgs): Promise<void> {
  const addEmptyLayerMutation = createMutationAddEmptyLayerSection({
    layerId,
    rootId,
    sectionId,
    name: `Empty layer for section ${sectionId}`,
  });
  const floorplanGenerationMutation =
    createMutationGenerateFloorplansForPointCloud({
      rootId,
      name: `Floor plan generation element for layer ${layerId}`,
      uri: pointcloudElement.uri,
      layerId,
      md5Hash: pointcloudElement.md5Hash,
      fileName: pointcloudElement.fileName,
      fileSize: pointcloudElement.fileSize,
      isPrivate,
      projectShareLink,
      measurementUnit,
      vendor,
    });

  await projectApiClient.applyMutations([
    addEmptyLayerMutation,
    floorplanGenerationMutation,
  ]);
}

/**
 * Tries to find a fitting enum value for an URL value provided by the user.
 *
 * @param featureValue the feature flag value provided by the user
 * @returns The enum value for the parsed string, undefined if no value was provided
 * @throws When a value is provided, but no matching vendor was found.
 */
function parseVendorFromFeatureValue(
  featureValue: string | undefined | null,
): FloorplanServiceVendor | undefined {
  if (!featureValue) {
    return;
  }

  const vendor = Object.values(FloorplanServiceVendor).find(
    (value) => value.toLowerCase() === featureValue.toLowerCase(),
  );

  assert(
    vendor,
    "Invalid vendor value provided for FloorPlanVendor feature flag",
  );
  return vendor;
}

/**
 * Select the point cloud to use for the floor plan generation, giving priority to the project point cloud.
 *
 * @param referenceElement The element to use as reference to find the children pointcloud
 * @returns The point cloud to use for the floor plan generation
 */
function selectPointCloudForFloorplan(referenceElement: IElement | undefined) {
  return ({
    iElements,
  }: OnlyIElementsStore): IElementGenericPointCloud | undefined =>
    selectChildDepthFirst(
      referenceElement,
      (el: IElement): el is IElementGenericPointCloud => {
        if (!isIElementGenericPointCloud(el)) return false;
        const pointCloudType = selectPointCloudType(el)({ iElements });
        return (
          pointCloudType === PointCloudType.project ||
          pointCloudType === PointCloudType.mobile
        );
      },
    )({ iElements });
}
